import React, { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
// import TablePagination from '@mui/material/TablePagination';
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import {
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  ListItemIcon,
  Rating,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import Loader from "layout/Loader";
import { CheckRTL } from "Helpers/CheckRTL";
import LoginIcon from "@mui/icons-material/Login";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { UserModuleProfileService } from "subModule/src/core/services";
import { GetUserByIdResponse } from "subModule/src/services/user/interface";
import { enqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { clearUserInfo, selectUserInfo } from "features/user/userSlice";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import StarIcon from "@mui/icons-material/Star";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PaymentIcon from "@mui/icons-material/Payment";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EastIcon from "@mui/icons-material/East";
// import { useNavigate } from "react-router-dom";
import { ReactComponent as ArrowUp } from "assets/arrow-up.svg";
import { ReactComponent as ArrowDown } from "assets/arrow-down.svg";
import { checkUrl } from "Helpers/CheckUrls";
import { truncDynCell } from "common/Table/truncDynCell";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import tronstrideIcon from "assets/tronstride.png";
import { clearSubscriptionStatuses } from "features/subscription/subscriptionSlice";
import { shouldShowIntegrationRetry } from "Helpers/integrationLogRetry";

interface Column {
  id: string;
  label: string;
  numeric: boolean;
  maxWidth?: number | string;
  width?: number | string;
}

const toCssSize = (value?: number | string) => {
  if (value == null) return undefined;
  return typeof value === "number" ? `${value}px` : value;
};

/** Shared header typography for every table column. */
const TABLE_HEADER_SX = {
  fontFamily: "Saira, sans-serif",
  fontWeight: 600,
  fontSize: "13px",
  color: "#212B36",
  lineHeight: 1.35,
} as const;

const getColumnWidthSx = (column: Column): Record<string, any> => {
  // Id stays compact and single-line; other columns use minWidth only so the
  // table can grow to 100% width instead of being capped by maxWidth.
  if (column.id === "id") {
    return {
      width: "72px",
      whiteSpace: "nowrap",
      lineHeight: 1.35,
    };
  }

  if (column.id === "multiAction") {
    // Mid-size checkbox col — width/min/max must all match or fixed-layout stretches it.
    return {
      width: "46px !important",
      minWidth: "46px !important",
      maxWidth: "46px !important",
      padding: "0 6px !important",
      whiteSpace: "nowrap",
      lineHeight: 1.35,
      boxSizing: "border-box",
      overflow: "hidden",
    };
  }

  if (column.id === "inventoryMode") {
    return {
      width: "96px",
      minWidth: "88px",
      whiteSpace: "nowrap",
      lineHeight: 1.35,
    };
  }

  if (column.id === "actions") {
    const preferred = toCssSize(column.width ?? column.maxWidth);
    return {
      width: preferred ?? "64px",
      minWidth: "56px",
      maxWidth: preferred ?? "80px",
      paddingLeft: "4px",
      paddingRight: "4px",
      whiteSpace: "nowrap",
      lineHeight: 1.35,
    };
  }

  if (column.id === "favorite") {
    return {
      width: "48px",
      minWidth: "48px",
      maxWidth: "52px",
      paddingLeft: "4px",
      paddingRight: "4px",
      textAlign: "center !important",
      whiteSpace: "nowrap",
      lineHeight: 1.35,
    };
  }

  if (column.id === "active") {
    const preferred = toCssSize(column.width ?? column.maxWidth);
    return {
      width: preferred ?? "100px",
      minWidth: "88px",
      maxWidth: preferred ?? "120px",
      whiteSpace: "nowrap",
      lineHeight: 1.35,
    };
  }

  if (column.id === "loginAsUser") {
    return {
      width: "180px",
      minWidth: "160px",
      maxWidth: "220px",
      whiteSpace: "normal",
      overflow: "visible",
      verticalAlign: "middle",
      lineHeight: 1.35,
    };
  }

  if (column.id === "integrationStatus") {
    return {
      width: "106px",
      minWidth: "88px",
      maxWidth: "110px",
      textAlign: "center !important",
      whiteSpace: "nowrap",
      lineHeight: 1.35,
    };
  }

  // Email / username columns — keep on one line; truncate via cell content.
  if (
    column.id === "email" ||
    column.id === "userName" ||
    column.id === "userNameClient" ||
    column.id === "userEmail"
  ) {
    const preferred = toCssSize(column.width ?? column.maxWidth);
    return {
      width: preferred ?? "18%",
      minWidth: "180px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      lineHeight: 1.35,
    };
  }

  // With table-layout: fixed these are proportional hints; leftover space is
  // still distributed across columns so the table always fills 100%.
  const preferred = toCssSize(column.width ?? column.maxWidth);
  return {
    width: preferred ?? "auto",
    maxWidth: preferred ?? undefined,
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    overflow: "hidden",
    textOverflow: "unset",
    lineHeight: 1.35,
  };
};

interface BackendPaginationTableProps {
  columns: Column[];
  data: any[];
  // stations: any[];
  showCheckbox: boolean;
  showActions: boolean;
  enableSorting: boolean;
  showHeader: boolean;
  showSearch: boolean;
  showFilter: boolean;
  showCreateButton: boolean;
  // actions:  any[];
  tableData?: any;
  actions?: {
    label: string;
    onClick: (value: any) => void;
    icon?: React.ReactNode;
    /** When set, action is shown in the row menu only if this returns true */
    show?: (row: any) => boolean;
  }[];
  headerComponent?: React.ReactNode;
  handleChangePage: (field: number) => void;
  handleChangeRows: (field: number) => void;
  onChangeStatus?: (row: any) => void;
  pageNo: number;
  pageSize: number;
  rowClick?: boolean;
  showRowView?: (row: any) => void;
  assignUnassignSingleEntity?: (isChecked: boolean, row: any) => void;
  assignUnassignAllEntities?: (isChecked: boolean) => void;
  allEntitiesAssigned?: unknown;
  loadingSearch?: boolean;
  assignPrivilege?: boolean;
  unassignPrivilege?: boolean;
  checkBoxAll?: string;
  dispatch?: any;
  displayDefault?: boolean;
  menuOpen?: (row: any) => void;
  markFavourite?: (row: any) => void;
  currency?: string;
  disableDefault?: boolean;
  hidePagination?: boolean;
  handleMultiAction?: (isChecked: boolean, row: any) => void;
  selectedRows?: any[];
  noFilter?: boolean;
  HideStickyHeader?: boolean;
  serverSideSorting?: boolean;
  sortOrderBy?: string;
  sortOrder?: "asc" | "desc";
  onSortChange?: (orderBy: string, order: "asc" | "desc") => void;
  sortableColumnIds?: string[];
  onIntegrationLogRetry?: (row: any) => void;
  integrationRetryLoadingId?: number | null;
  /** Optional nested/expandable rows (e.g. rack → shelf → bin). */
  expandable?: boolean;
  expandedRowIds?: Record<number, boolean>;
  onToggleExpand?: (row: any) => void;
  renderExpandedContent?: (row: any) => React.ReactNode;
  isRowExpandable?: (row: any) => boolean;
  expandLoadingIds?: Record<number, boolean>;
}

const BackendPaginationTable: React.FC<BackendPaginationTableProps> = ({
  columns,
  data,
  showCheckbox,
  showActions,
  enableSorting,
  showHeader,
  showSearch,
  actions,
  headerComponent,
  handleChangePage,
  handleChangeRows,
  tableData,
  // pageNo,
  pageSize,
  onChangeStatus,
  rowClick,
  showRowView,
  assignUnassignSingleEntity,
  assignUnassignAllEntities,
  allEntitiesAssigned,
  loadingSearch,
  assignPrivilege,
  unassignPrivilege,
  checkBoxAll,
  dispatch,
  displayDefault,
  // stations,
  menuOpen,
  markFavourite,
  currency,
  disableDefault,
  hidePagination,
  handleMultiAction,
  selectedRows,
  HideStickyHeader,
  serverSideSorting,
  sortOrderBy: controlledSortOrderBy,
  sortOrder: controlledSortOrder,
  onSortChange,
  sortableColumnIds,
  onIntegrationLogRetry,
  integrationRetryLoadingId,
  expandable = false,
  expandedRowIds,
  onToggleExpand,
  renderExpandedContent,
  isRowExpandable,
  expandLoadingIds,
}) => {
  // const navigate = useNavigate();
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("");
  const [selected, setSelected] = useState<number[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(pageSize);
  const [selectedRow, setSelectedRow] = useState({});
  const {
    control,
    formState: { errors },
    trigger,
    watch,
    reset,
    setValue,
  } = useForm<{ [key: string]: any }>({ defaultValues: {} });
  // const [openModal, setOpenModal] = useState(false);
  const loggedInUserData = useSelector(selectUserInfo);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { t, i18n } = useTranslation();
  const isRTL = CheckRTL();

  const integrationModuleLabel = (value: any) =>
    (
      ({
        1: "BAYANAT",
        2: "VEHICLE",
        3: "JOBCARD",
        4: "WORKORDER",
        5: "INVOICE",
        6: "ESTIMATE",
        7: "ACCOUNT",
        8: "EINVOICE",
        9: "BILLING",
      }) as Record<number, string>
    )[Number(value)] || "";
  const integrationActionLabel = (value: any) =>
    (
      ({
        1: "VALIDATE_VIN",
        2: "AUTH_LOGIN",
        3: "FETCH_VEHICLE_BY_VIN",
        4: "SEND_JOBCARD",
        5: "SEND_WORKORDER",
        6: "SEND_INVOICE",
        7: "CANCEL_JOBCARD",
        8: "CANCEL_WORKORDER",
        9: "SEND_ESTIMATE",
        10: "CREATE_ACCOUNT",
        11: "UPDATE_ACCOUNT",
        12: "ARCHIVE_ACCOUNT",
        13: "ACTIVE_ACCOUNT",
        14: "CREATE_VEHICLE",
        15: "UPDATE_VEHICLE",
        16: "BULK_CREATE_UPDATE_VEHICLE",
        17: "ARCHIVE_VEHICLE",
        18: "SEND_EINVOICE",
        19: "SEND_EVENT",
      }) as Record<number, string>
    )[Number(value)] || "";
  const getIntegrationLogStatus = (row: any) =>
    Number(row?.status ?? row?.integrationStatus);
  const getIntegrationLogCreatedAt = (row: any) =>
    row?.createdAt ||
    row?.createdDate ||
    row?.attributes?.createdAt ||
    row?.attributes?.createdDate ||
    null;
  const formatIntegrationDirectionLabel = (value?: string | null) => {
    if (!value) return "";
    if (value === "FlexPrice") return t("billingApp");
    if (value === "FMS") return t("FleetOS");
    return value;
  };
  /** API: OPEN = 1, BIN = 2 */
  const getStoreInventoryModeValue = (row: any): 1 | 2 | null => {
    const raw = row?.inventoryMode;
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    if (n === 1 || n === 2) return n as 1 | 2;
    const s = String(raw).toLowerCase();
    if (s === "open") return 1;
    if (s === "bin") return 2;
    return null;
  };
  const pickIntegrationRefValue = (...values: unknown[]) => {
    for (const value of values) {
      if (value === null || value === undefined || value === "") continue;
      if (typeof value === "string" || typeof value === "number") {
        return String(value);
      }
    }
    return "";
  };
  /** Document/reference no. from integration request payload by module. */
  const getIntegrationReferenceNumber = (row: any) => {
    const module = Number(row?.module);
    const req = row?.requestPayload || {};
    const payload =
      req?.payload && typeof req.payload === "object" ? req.payload : {};
    const invoice0 = Array.isArray(payload?.Invoice)
      ? payload.Invoice[0]
      : null;

    switch (module) {
      case 3: // JOBCARD
        return pickIntegrationRefValue(
          req.jc,
          req.jobCardNo,
          req.JobCardNumber,
          payload.JobCardNumber,
          invoice0?.JobCardNumber,
        );
      case 4: // WORKORDER
        return pickIntegrationRefValue(
          req.workOrderNo,
          req.WorkOrderNumber,
          payload.WorkOrderNumber,
          invoice0?.WorkOrderNumber,
        );
      case 5: // INVOICE
      case 8: // EINVOICE
        return pickIntegrationRefValue(
          req.invoiceNo,
          req.invoiceNumber,
          payload.invoiceNumber,
          invoice0?.invoiceNumber,
        );
      case 6: // ESTIMATE
        return pickIntegrationRefValue(
          req.estimateNo,
          req.EstimateNumber,
          payload.EstimateNumber,
          payload.Name,
          invoice0?.EstimateNumber,
        );
      default:
        return pickIntegrationRefValue(
          req.jc,
          req.jobCardNo,
          req.JobCardNumber,
          req.workOrderNo,
          req.WorkOrderNumber,
          req.invoiceNo,
          req.invoiceNumber,
          req.estimateNo,
          req.EstimateNumber,
          payload.JobCardNumber,
          payload.WorkOrderNumber,
          payload.EstimateNumber,
          payload.Name,
          payload.invoiceNumber,
          invoice0?.JobCardNumber,
          invoice0?.WorkOrderNumber,
          invoice0?.EstimateNumber,
          invoice0?.invoiceNumber,
        );
    }
  };
  const stationDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<string>("");
  const [dropdownPageNo, setDropdownPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [stationDropdownBusinessId, setStationDropdownBusinessId] = useState<
    number | undefined
  >(undefined);

  const { data: stationsData, refetch: stationsRefetch } =
    useGetStationDropdownService({
      pageNumber: dropdownPageNo,
      size: 50,
      name: dropdownSearchTerm,
      businessId: stationDropdownBusinessId,
    });
  const stations = useMemo(
    () =>
      (stationsData?.pages[0]?.data?.content || []).map(
        (item: { id: number; name: string; code?: string }) => ({
          value: item.id,
          // loginAsUser POS station picker: show id only (not code/name)
          label: String(item.id),
        }),
      ),
    [stationsData],
  );

  const handleDropdownActions = (
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPageNo(1);
      setDropdownSearchTerm(search || "");
      if (search) setSearchLoading("stations");
    } else {
      setDropdownPageNo(dropdownPageNo + 1);
      setSearchLoading("stations");
    }
    if (stationDebounceTimer.current)
      clearTimeout(stationDebounceTimer.current);
    stationDebounceTimer.current = setTimeout(() => {
      stationsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };

  const handleClickmenu = (event: React.MouseEvent<HTMLElement>, row: any) => {
    menuOpen?.(row);
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRequestSort = (property: string) => {
    if (
      serverSideSorting &&
      sortableColumnIds?.length &&
      !sortableColumnIds.includes(property)
    ) {
      return;
    }

    const isAsc =
      (serverSideSorting ? controlledSortOrderBy : orderBy) === property &&
      (serverSideSorting ? controlledSortOrder : order) === "asc";
    const nextOrder: "asc" | "desc" = isAsc ? "desc" : "asc";

    if (serverSideSorting) {
      onSortChange?.(property, nextOrder);
      return;
    }

    setOrder(nextOrder);
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data.map((n, index) => index);
      setSelected(newSelected);
      assignUnassignAllEntities?.(true);
      return;
    } else {
      assignUnassignAllEntities?.(false);
    }
    setSelected([]);
  };

  const handleClick = (
    event: React.MouseEvent<unknown>,
    index: number,
    row: any,
  ) => {
    const target = event.target as HTMLInputElement;
    const selectedIndex = selected.indexOf(index);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, index);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
    assignUnassignSingleEntity?.(target.checked, row);
  };

  // const handleChangePage1 = (event: unknown, newPage: number) => {
  //      handleChangePage(newPage)
  //     setPage(newPage+1);
  // };

  const handleChangePage1 = (action: string) => {
    // let { pagination } = this.props;

    if (tableData) {
      if (action === "first") {
        if (tableData?.pageable?.pageNumber > 0) {
          handleChangePage(0);
        }
      } else if (action === "prev") {
        if (tableData.pageable && tableData?.pageable?.pageNumber > 0) {
          handleChangePage(tableData?.pageable?.pageNumber - 1);
        }
      } else if (action === "next") {
        if (
          !tableData?.last
          //  && tableData.page &&
          // tableData.lastPage &&
          // tableData.lastPage > tableData.page
        ) {
          handleChangePage(tableData?.pageable?.pageNumber + 1);
        }
      } else if (action === "last") {
        if (
          !tableData?.last
          // && tableData.lastPage &&
          // tableData.lastPage > tableData.page
        ) {
          handleChangePage(tableData?.totalPages - 1);
        }
      }
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    handleChangeRows(parseInt(event.target.value));
    setPage(0);
  };

  const isSelected = (index: number) => selected.indexOf(index) !== -1;
  // const emptyRows = Math.max(0, rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage));

  const stableSort = (array: any[], comparator: (a: any, b: any) => number) => {
    if (!Array.isArray(array)) return [];

    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [any, number],
    );
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const isSortValueEmpty = (value: unknown) =>
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "string" && value.trim() === "");

  const isColumnSortValueEmpty = (value: unknown, orderBy: string) => {
    if (
      orderBy === "stockLevel" ||
      orderBy === "baseItemQuantity" ||
      orderBy === "stockQuantity"
    ) {
      if (isSortValueEmpty(value)) return true;
      const num = parseFloat(String(value));
      return Number.isNaN(num) || num === 0;
    }
    return isSortValueEmpty(value);
  };

  const DATE_SORT_COLUMNS = new Set([
    "startDate",
    "endDate",
    "packageTime",
    "serviceTime",
    "creditPeriod",
    "date",
    "paymentDate",
    "recipientDate",
    "poDate",
    "invoiceDate",
    "lastServiceDate",
    "lastVisitDate",
    "creditDebitDate",
    "receivedDate",
    "dateOfIncorporation",
    "transferDate",
    "createdAt",
    "creditNoteDate",
    "transactionDate",
    "subscriptionPlanExpiry",
    "oldPlanStartDate",
    "oldPlanEndDate",
  ]);

  const NUMERIC_SORT_COLUMNS = new Set([
    "price",
    "itemCost",
    "stockLevel",
    "baseItemQuantity",
    "stockQuantity",
    "minQuantity",
    "maxQuantity",
    "minPrice",
  ]);

  const getRowSortValue = (row: any, orderBy: string) => {
    if (orderBy === "stockLevel") {
      return row?.baseItemQuantity ?? row?.stockLevel ?? row?.stockQuantity;
    }
    return row?.[orderBy];
  };

  const compareSortValues = (
    aVal: unknown,
    bVal: unknown,
    orderBy: string,
    sortOrder: "asc" | "desc",
  ) => {
    const aEmpty = isColumnSortValueEmpty(aVal, orderBy);
    const bEmpty = isColumnSortValueEmpty(bVal, orderBy);
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;

    let comparison = 0;
    if (DATE_SORT_COLUMNS.has(orderBy)) {
      const aTime = new Date(String(aVal)).getTime();
      const bTime = new Date(String(bVal)).getTime();
      if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
        comparison = aTime - bTime;
      } else {
        const aStr = String(aVal);
        const bStr = String(bVal);
        if (aStr < bStr) comparison = -1;
        else if (aStr > bStr) comparison = 1;
      }
    } else if (
      NUMERIC_SORT_COLUMNS.has(orderBy) ||
      (typeof aVal === "number" && typeof bVal === "number")
    ) {
      const aNum = parseFloat(String(aVal));
      const bNum = parseFloat(String(bVal));
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        comparison = aNum - bNum;
      } else {
        const aStr = String(aVal);
        const bStr = String(bVal);
        if (aStr < bStr) comparison = -1;
        else if (aStr > bStr) comparison = 1;
      }
    } else {
      const aStr = String(aVal);
      const bStr = String(bVal);
      if (aStr < bStr) comparison = -1;
      else if (aStr > bStr) comparison = 1;
    }

    return sortOrder === "desc" ? -comparison : comparison;
  };

  const getComparator = (sortOrder: "asc" | "desc", orderBy: string) => {
    return (a: any, b: any) =>
      compareSortValues(
        getRowSortValue(a, orderBy),
        getRowSortValue(b, orderBy),
        orderBy,
        sortOrder,
      );
  };

  const activeSortOrderBy = serverSideSorting
    ? controlledSortOrderBy || ""
    : orderBy;
  const activeSortOrder = serverSideSorting
    ? controlledSortOrder || "asc"
    : order;

  const visibleRows = useMemo(() => {
    const tableRows = Array.isArray(data)
      ? data.filter((row) => row != null)
      : [];

    if (serverSideSorting) {
      if (enableSorting && activeSortOrderBy) {
        return stableSort(
          tableRows,
          getComparator(activeSortOrder, activeSortOrderBy),
        );
      }
      return tableRows;
    }
    if (enableSorting && orderBy) {
      return stableSort(tableRows, getComparator(order, orderBy));
    }
    return tableRows;
  }, [
    data,
    enableSorting,
    order,
    orderBy,
    serverSideSorting,
    activeSortOrderBy,
    activeSortOrder,
  ]);
  const getUserById = (
    id: number,
    server: string,
    stationId: string | number,
  ) => {
    UserModuleProfileService.getUserByID(id)
      .then((res: GetUserByIdResponse) => {
        if (res.success) {
          if (res?.data?.loginToken) {
            const stationParam = stationId ? `&stationId=${stationId}` : ""; // Add stationId if provided
            const ownerTok = localStorage.getItem("ACCESS_TOKEN");
            const ownerUi = localStorage.getItem("userInfo");
            if (ownerTok) localStorage.setItem("loginAsOwnerToken", ownerTok);
            if (ownerUi) localStorage.setItem("loginAsOwnerUserInfo", ownerUi);
            if (server == window.location.origin) {
              window.location.href =
                `${server}` +
                `?loginAsUser=${res.data.loginToken}` +
                `&auth=${localStorage.getItem("ACCESS_TOKEN")}` +
                stationParam;
              dispatch?.(clearUserInfo());
              dispatch?.(clearSubscriptionStatuses());
              ["userInfo", "selectedSubItem", "ACCESS_TOKEN"].forEach((item) =>
                localStorage.removeItem(item),
              );
            } else {
              window.open(
                `${server}` +
                  `?loginAsUser=${res.data.loginToken}` +
                  `&auth=${localStorage.getItem("ACCESS_TOKEN")}` +
                  stationParam,
                "_blank",
              );
            }
            reset();
          } else {
            reset();
            enqueueSnackbar(<AlertMessages text={t("noTokenFound")} />, {
              variant: "error",
            });
          }
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  // Checkbox/assignment tables keep spaced rows. Listing pages always use a
  // collapsed table so `rowClick={false}` (no edit privilege) cannot switch
  // the page into the stacked-card look seen in production builds.
  const isSeparatedRowStyle = Boolean(showCheckbox) && !rowClick;
  return (
    <Box className="w-[100%] ">
      <Paper
        className="w-[100%] mb-[2]"
        sx={{ boxShadow: "none", background: themeColors?.appBg }}
      >
        {showHeader && showSearch ? (
          <Box
            className="table-header-toolbar"
            sx={{
              minHeight: isSeparatedRowStyle ? "48px" : "50px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              width: "100%",
              // margin: "5px 0px",
              gap: 1,
              // Search filter grids share the row and leave room for action buttons
              "& > .MuiGrid-container, & > .table-header-search": {
                flex: "1 1 360px",
                width: "auto !important",
                maxWidth: "100%",
                minWidth: 0,
                alignSelf: "flex-start",
              },
              // Keep download/upload (and similar) actions grouped on the right
              "& > .MuiGrid-item": {
                flex: "0 0 auto",
                width: "auto !important",
                maxWidth: "none",
                marginInlineStart: "auto",
                alignSelf: "flex-start",
              },
              "& > .MuiGrid-item ~ .MuiGrid-item": {
                marginInlineStart: 0,
              },
              // Follow-on action roots (e.g. upload menu not wrapped in Grid item)
              "& > .MuiGrid-item ~ *:not(.MuiGrid-container):not(.MuiGrid-item):not(.table-header-search)":
                {
                  flex: "0 0 auto",
                },
              // Services/Packages/Items-style action groups (download/upload/bulk)
              "& > .table-header-actions": {
                flex: "0 0 auto",
                marginInlineStart: "auto",
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "flex-start",
                alignSelf: "flex-start",
                justifyContent: "flex-end",
                gap: 1,
                "& .MuiButton-root": { marginInlineStart: 0 },
              },
            }}
          >
            {headerComponent}
          </Box>
        ) : null}
        {loadingSearch ? (
          <Loader />
        ) : (
          <>
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <TableContainer
                sx={{
                  width: "100%",
                  ...(isSeparatedRowStyle
                    ? {
                        borderRadius: "0px",
                        border: "none",
                        overflow: "visible",
                      }
                    : { borderRadius: "8px", overflow: "visible" }),
                }}
              >
                <Table
                  className="w-full"
                  size="small"
                  sx={{
                    display: "table",
                    tableLayout: "fixed",
                    width: "100%",
                    minWidth: "100%",
                    borderCollapse: isSeparatedRowStyle
                      ? "separate"
                      : "collapse",
                    borderSpacing: isSeparatedRowStyle ? "0 8px" : 0,
                    "& .multi-action-col": {
                      width: "46px !important",
                      minWidth: "46px !important",
                      maxWidth: "46px !important",
                      paddingLeft: "6px !important",
                      paddingRight: "6px !important",
                      boxSizing: "border-box",
                      overflow: "hidden",
                    },
                  }}
                >
                  <TableHead
                    sx={{
                      backgroundColor: "#fff",
                      display: "table-header-group",
                      "& .MuiTableCell-head": {
                        ...TABLE_HEADER_SX,
                        backgroundColor: "#fff",
                      },
                    }}
                    className={`${HideStickyHeader ? "" : "sticky top-0"} z-[20]`}
                  >
                    <TableRow
                      sx={
                        isSeparatedRowStyle
                          ? {
                              height: "48px",
                              backgroundColor: "white",
                              ".MuiTableCell-head": {
                                ...TABLE_HEADER_SX,
                                textAlign: "left",
                                border: "0px",
                                "&: last-child": {
                                  borderRadius: "0 8px 8px 0",
                                },
                              },
                            }
                          : undefined
                      }
                    >
                      {showCheckbox && (
                        <TableCell
                          padding="checkbox"
                          className={`${isSeparatedRowStyle ? "border-none rounded-l-[8px]" : ""}`}
                          id={`${checkBoxAll}`}
                        >
                          <Checkbox
                            // indeterminate={selected.length > 0 && selected.length < data.length}
                            // checked={data.length > 0 && selected.length === data.length}
                            checked={
                              allEntitiesAssigned &&
                              tableData?.content?.length !== 0
                                ? true
                                : false
                            }
                            onChange={handleSelectAllClick}
                            inputProps={{
                              "aria-label": "select all items",
                            }}
                            disabled={
                              tableData?.content?.length
                                ? assignPrivilege && !allEntitiesAssigned
                                  ? false
                                  : unassignPrivilege && allEntitiesAssigned
                                    ? false
                                    : true
                                : true
                            }
                            sx={{
                              opacity: 1,
                              "& svg": { fill: themeColors?.primary },
                              "&.Mui-disabled": {
                                opacity: 0.3,
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {expandable && (
                        <TableCell
                          padding="checkbox"
                          sx={{ width: 40, minWidth: 40 }}
                        />
                      )}
                      {showActions &&
                        columns.map((column, columnIndex) => (
                          <TableCell
                            key={columnIndex}
                            sx={{
                              textAlign:
                                column.id === "integrationStatus"
                                  ? "center !important"
                                  : isRTL
                                    ? "right !important"
                                    : "left !important",
                              ...getColumnWidthSx(column),
                            }}
                            padding={
                              column.id === "actions" ? "normal" : "normal"
                            }
                            sortDirection={
                              activeSortOrderBy === column.id
                                ? activeSortOrder
                                : false
                            }
                            className={`font-Saira ${column.id === "multiAction" ? "multi-action-col" : ""} ${columnIndex !== columns.length - 1 && !isSeparatedRowStyle ? "borderRightApplied" : ""}`}
                          >
                            {column.id !== "actions" &&
                              column.id !== "multiAction" &&
                              (enableSorting &&
                              (!sortableColumnIds?.length ||
                                sortableColumnIds.includes(column.id)) ? (
                                <TableSortLabel
                                  active={activeSortOrderBy === column.id}
                                  direction={
                                    activeSortOrderBy === column.id
                                      ? activeSortOrder
                                      : "asc"
                                  }
                                  onClick={() => handleRequestSort(column.id)}
                                  sx={{
                                    ...TABLE_HEADER_SX,
                                    whiteSpace:
                                      column.id === "id" ||
                                      column.id === "integrationStatus"
                                        ? "nowrap"
                                        : "normal",
                                    wordBreak: "normal",
                                    overflowWrap:
                                      column.id === "id"
                                        ? "normal"
                                        : "break-word",
                                    maxWidth: "100%",
                                    "&.MuiTableSortLabel-root": {
                                      ...TABLE_HEADER_SX,
                                    },
                                    "&.Mui-active": {
                                      color: "#212B36",
                                      "& .MuiTableSortLabel-icon": {
                                        color: "#212B36",
                                      },
                                    },
                                    "&:hover": {
                                      color: "#212B36",
                                    },
                                    ...(column.id === "integrationStatus"
                                      ? {
                                          display: "flex",
                                          justifyContent: "center",
                                          width: "100%",
                                        }
                                      : {}),
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      ...TABLE_HEADER_SX,
                                      whiteSpace:
                                        column.id === "id"
                                          ? "nowrap"
                                          : "normal",
                                      overflowWrap:
                                        column.id === "id"
                                          ? "normal"
                                          : "break-word",
                                    }}
                                  >
                                    {t(column.label)}
                                    {column.id === "inQty" && (
                                      <ArrowUp
                                        style={{
                                          fill: "#17AE36",
                                          width: "14px",
                                          height: "14px",
                                        }}
                                      />
                                    )}
                                    {column.id === "outQty" && (
                                      <ArrowDown
                                        style={{
                                          fill: "red",
                                          width: "14px",
                                          height: "14px",
                                        }}
                                      />
                                    )}
                                  </Box>
                                </TableSortLabel>
                              ) : (
                                <Box component="span" sx={TABLE_HEADER_SX}>
                                  {t(column.label)}
                                </Box>
                              ))}
                            {column.id === "multiAction" && (
                              <Box
                                className="flex justify-center items-center"
                                sx={{ width: "100%" }}
                                onClick={(
                                  event: React.MouseEvent<
                                    HTMLDivElement,
                                    MouseEvent
                                  >,
                                ) => {
                                  event.stopPropagation();
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    if (checked)
                                      visibleRows.forEach((row: any) => {
                                        handleMultiAction?.(true, row);
                                      });
                                    else
                                      visibleRows.forEach((row: any) => {
                                        handleMultiAction?.(false, row);
                                      });
                                  }}
                                  checked={
                                    visibleRows.length > 0 &&
                                    visibleRows.every((row: any) =>
                                      selectedRows?.some(
                                        (r: any) => r.id === row.id,
                                      ),
                                    )
                                  }
                                  sx={{
                                    p: 0.5,
                                    "& svg": { fill: themeColors?.primary },
                                  }}
                                />
                              </Box>
                            )}
                            {column.id === "actions" && (
                              <Box
                                sx={{
                                  ...TABLE_HEADER_SX,
                                  whiteSpace: "nowrap",
                                  width: "100%",
                                  textAlign: isRTL ? "right" : "left",
                                }}
                              >
                                {t(column.label)}
                              </Box>
                            )}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={
                            columns.length +
                            (showCheckbox ? 1 : 0) +
                            (expandable ? 1 : 0)
                          }
                          align="center"
                          className="py-3"
                        >
                          <Typography
                            className="font-Saira font-[500] text-[15px] italic"
                            sx={{ color: themeColors?.primary }}
                          >
                            {t("noRecordFound")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleRows.map((row, index) => {
                        const rowExpanded =
                          expandable && !!expandedRowIds?.[row?.id];
                        const canExpandRow =
                          expandable &&
                          (isRowExpandable ? isRowExpandable(row) : true);
                        return (
                          <React.Fragment key={row?.id ?? row?.uuid ?? index}>
                            <TableRow
                              hover
                              role="checkbox"
                              aria-checked={isSelected(
                                page * rowsPerPage + index,
                              )}
                              tabIndex={-1}
                              selected={isSelected(page * rowsPerPage + index)}
                              sx={
                                !isSeparatedRowStyle
                                  ? {
                                      backgroundColor:
                                        index % 2 === 0
                                          ? themeColors?.appBg
                                          : "#fff",
                                      cursor: rowClick ? "pointer" : "default",
                                    }
                                  : {
                                      height: "48px",
                                      backgroundColor: "white",
                                      padding: "16px",
                                      cursor: "pointer",
                                      "&:last-child td, &:last-child th": {
                                        border: 0,
                                      },
                                    }
                              }
                            >
                              {showCheckbox && (
                                <TableCell
                                  padding="checkbox"
                                  className={`${isSeparatedRowStyle ? "border-none rounded-l-[8px]" : ""}`}
                                >
                                  <Checkbox
                                    onClick={(event) =>
                                      handleClick(
                                        event,
                                        page * rowsPerPage + index,
                                        row,
                                      )
                                    }
                                    checked={row.linked ? true : false}
                                    inputProps={{
                                      "aria-labelledby": `enhanced-table-checkbox-${index}`,
                                    }}
                                    // disabled={
                                    //   assignPrivilege && !row.linked
                                    //     ? false
                                    //     : unassignPrivilege && row.linked
                                    //       ? false
                                    //       : true
                                    // }
                                    disabled={(() => {
                                      if (
                                        row.linked &&
                                        disableDefault &&
                                        row.default
                                      )
                                        return true;
                                      if (
                                        (assignPrivilege && !row.linked) ||
                                        (unassignPrivilege && row.linked)
                                      ) {
                                        return false;
                                      } else {
                                        return true;
                                      }
                                    })()}
                                    sx={{
                                      opacity: 1,
                                      "& svg": { fill: themeColors?.primary },
                                      "&.Mui-disabled": {
                                        opacity: 0.3,
                                      },
                                    }}
                                  />
                                </TableCell>
                              )}
                              {expandable && (
                                <TableCell
                                  padding="checkbox"
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ width: 40, minWidth: 40 }}
                                >
                                  {canExpandRow ? (
                                    <IconButton
                                      size="small"
                                      sx={{
                                        p: 0.25,
                                        transform: isRTL
                                          ? "scaleX(-1)"
                                          : undefined,
                                      }}
                                      onClick={() => onToggleExpand?.(row)}
                                      aria-label="expand row"
                                    >
                                      {rowExpanded ? (
                                        <ExpandMoreIcon fontSize="small" />
                                      ) : (
                                        <ChevronRightIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  ) : null}
                                </TableCell>
                              )}
                              {showActions &&
                                columns.map((column, columnIndex) => (
                                  <TableCell
                                    key={columnIndex}
                                    onClick={() => {
                                      rowClick &&
                                      column.id !== "active" &&
                                      column.id !== "actions"
                                        ? showRowView?.(row)
                                        : "";
                                    }}
                                    className={`font-Saira font-[400] text-[13px] ${column.id === "multiAction" ? "multi-action-col" : ""} ${columnIndex !== columns.length - 1 ? "borderRightApplied" : ""} ${
                                      column.id === "integrationStatus"
                                        ? "text-center"
                                        : isRTL
                                          ? "text-right"
                                          : "text-left"
                                    }`}
                                    sx={
                                      isSeparatedRowStyle
                                        ? {
                                            border: "0 !important",
                                            "&: last-child": {
                                              borderRadius: "0 8px 8px 0",
                                            },
                                            ...getColumnWidthSx(column),
                                          }
                                        : {
                                            ...getColumnWidthSx(column),
                                          }
                                    }
                                    padding={
                                      column.id === "actions" ||
                                      column.id === "favorite" ||
                                      column.id === "currentBalance" ||
                                      column.id === "balance" ||
                                      column.id === "multiAction"
                                        ? "none"
                                        : "normal"
                                    }
                                    align={
                                      column.id === "integrationStatus"
                                        ? "center"
                                        : "left"
                                    }
                                  >
                                    {column.id === "actions" ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: isRTL
                                            ? "flex-end"
                                            : "flex-start",
                                          alignItems: "center",
                                          width: "100%",
                                          gap: 0.75,
                                          flexWrap: "nowrap",
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {!displayDefault &&
                                        (row?.default ||
                                          row?.systemDefault ||
                                          row?.isDefault) ? (
                                          ""
                                        ) : actions?.length ? (
                                          <IconButton
                                            aria-label="more"
                                            id="long-button"
                                            aria-controls={
                                              open ? "long-menu" : undefined
                                            }
                                            aria-expanded={
                                              open ? "true" : undefined
                                            }
                                            aria-haspopup="true"
                                            size="small"
                                            onClick={(event) =>
                                              handleClickmenu(event, row)
                                            }
                                            sx={{ p: 0.5 }}
                                          >
                                            <Tooltip
                                              enterDelay={0}
                                              title={t("moreOptions")}
                                            >
                                              <MoreVertIcon fontSize="small" />
                                            </Tooltip>
                                          </IconButton>
                                        ) : null}
                                        <Menu
                                          slotProps={{
                                            root: {
                                              sx: {
                                                zIndex: 0,
                                              },
                                            },
                                          }}
                                          id="long-menu"
                                          disableScrollLock
                                          MenuListProps={{
                                            "aria-labelledby": "long-button",
                                          }}
                                          anchorEl={anchorEl}
                                          open={open}
                                          onClose={handleClose}
                                          PaperProps={{
                                            elevation: 0,
                                            sx: {
                                              overflow: "visible",
                                              filter:
                                                "drop-shadow(0px 2px 2px rgba(0,0,0,0.05))",
                                              // mt: 1.5,
                                              "& .MuiAvatar-root": {
                                                width: 32,
                                                height: 32,
                                                // ml: -0.5,
                                                // mr: 1,
                                              },
                                              "&::before": {
                                                content: '""',
                                                display: "block",
                                                position: "absolute",
                                                top: 0,
                                                left: 14,
                                                width: 10,
                                                height: 10,
                                                bgcolor: "background.paper",
                                                transform:
                                                  "translateY(-50%) rotate(45deg)",
                                                zIndex: 0,
                                              },
                                            },
                                          }}
                                        >
                                          {actions
                                            ?.filter(
                                              (option) =>
                                                !option.show ||
                                                option.show(selectedRow),
                                            )
                                            .map((option) => (
                                              <div
                                                key={option.label + index}
                                                onClick={handleClose}
                                              >
                                                <MenuItem
                                                  className="font-Saira font-[400] text-[14]"
                                                  onClick={() =>
                                                    option.onClick(selectedRow)
                                                  }
                                                >
                                                  <ListItemIcon>
                                                    {option.icon}
                                                  </ListItemIcon>
                                                  {t(option.label)}
                                                </MenuItem>
                                              </div>
                                            ))}
                                        </Menu>
                                      </Box>
                                    ) : column.id === "multiAction" ? (
                                      <Box
                                        className="flex justify-center items-center"
                                        sx={{ width: "100%" }}
                                        onClick={(
                                          event: React.MouseEvent<
                                            HTMLDivElement,
                                            MouseEvent
                                          >,
                                        ) => {
                                          event.stopPropagation();
                                        }}
                                      >
                                        <Checkbox
                                          size="small"
                                          onChange={(event) => {
                                            handleMultiAction?.(
                                              event.target.checked,
                                              row,
                                            );
                                          }}
                                          checked={selectedRows?.some(
                                            (r: any) => r.id === row.id,
                                          )}
                                          sx={{
                                            p: 0.5,
                                            "& svg": {
                                              fill: themeColors?.primary,
                                            },
                                          }}
                                        />
                                      </Box>
                                    ) : column.id === "lastVisitDate" ? (
                                      row[column.id] ? (
                                        (() => {
                                          const d = new Date(row[column.id]);
                                          if (Number.isNaN(d.getTime()))
                                            return "";
                                          const dd = String(
                                            d.getUTCDate(),
                                          ).padStart(2, "0");
                                          const mm = String(
                                            d.getUTCMonth() + 1,
                                          ).padStart(2, "0");
                                          const yyyy = d.getUTCFullYear();
                                          return `${dd}-${mm}-${yyyy}`;
                                        })()
                                      ) : (
                                        ""
                                      )
                                    ) : (column.id === "startDate" ||
                                        column.id === "endDate" ||
                                        column.id === "packageTime" ||
                                        column.id === "serviceTime" ||
                                        column.id === "creditPeriod" ||
                                        column.id === "date" ||
                                        column.id === "paymentDate" ||
                                        column.id === "recipientDate" ||
                                        column.id === "poDate" ||
                                        column.id === "invoiceDate" ||
                                        column.id === "lastServiceDate" ||
                                        column.id === "creditDebitDate" ||
                                        column.id === "receivedDate" ||
                                        column.id === "dateOfIncorporation" ||
                                        column.id === "transferDate") &&
                                      row[column.id] ? (
                                      row[column.id].split("T")[0]
                                    ) : column.id === "createdAt" ||
                                      column.id === "creditNoteDate" ||
                                      column.id === "transactionDate" ||
                                      column.id === "transactionDate" ? (
                                      getIntegrationLogCreatedAt(row) ||
                                      row[column.id] ? (
                                        new Intl.DateTimeFormat(i18n.language, {
                                          year: "numeric",
                                          month: "long",
                                          day: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                          second: "2-digit",
                                          numberingSystem:
                                            i18n.language === "ur" ||
                                            i18n.language === "ar"
                                              ? "arab"
                                              : "latn",
                                        }).format(
                                          new Date(
                                            getIntegrationLogCreatedAt(row) ||
                                              row[column.id],
                                          ),
                                        )
                                      ) : null
                                    ) : column.id ===
                                        "subscriptionPlanExpiry" ||
                                      column.id === "oldPlanStartDate" ||
                                      column.id === "oldPlanEndDate" ? (
                                      row?.stationPlan?.endDate ||
                                      row?.businessPlan?.endDate ||
                                      row[column.id] ? (
                                        truncDynCell(
                                          new Intl.DateTimeFormat(
                                            i18n.language,
                                            {
                                              year: "numeric",
                                              month: "long",
                                              day: "numeric",
                                              numberingSystem:
                                                i18n.language === "ur" ||
                                                i18n.language === "ar"
                                                  ? "arab"
                                                  : "latn",
                                            },
                                          ).format(
                                            new Date(
                                              row?.stationPlan?.endDate ||
                                                row?.businessPlan?.endDate ||
                                                row[column.id] ||
                                                "",
                                            ),
                                          ),
                                          column.maxWidth,
                                        )
                                      ) : null
                                    ) : column.id === "newStatus" ? (
                                      <Box
                                        className={`rounded-[50px] w-[fit-content] text-[11px] font-semibold px-[10px] py-[2px]
                                    ${
                                      row[column.id] === 1
                                        ? "bg-[#D1FAE5] text-[#065F46]"
                                        : row[column.id] === 2
                                          ? "bg-[#C5C5C5] text-[#040404]"
                                          : row[column.id] === 3
                                            ? "bg-[#C4C5E8] text-[#0007B4]"
                                            : row[column.id] === 4
                                              ? "bg-[#E9C9C9] text-[#BA1A1A]"
                                              : row[column.id] === 5
                                                ? "bg-[#F6D7D0] text-[#FD603B]"
                                                : "bg-[#C5C5C5] text-[#040404]"
                                    }
                                `}
                                      >
                                        {row[column.id] === 1
                                          ? t("active")
                                          : row[column.id] === 2
                                            ? t("inactive")
                                            : row[column.id] === 3
                                              ? t("trial")
                                              : row[column.id] === 4
                                                ? t("cancelled")
                                                : row[column.id] === 5
                                                  ? t("suspended")
                                                  : "---"}
                                      </Box>
                                    ) : column.id === "retentionStatus" ? (
                                      (() => {
                                        const retentionStatus = Number(
                                          row.status ??
                                            row.retentionStatus ??
                                            0,
                                        );
                                        const retentionConfig: Record<
                                          number,
                                          { label: string; className: string }
                                        > = {
                                          1: {
                                            label: t("retentionActive"),
                                            className:
                                              "bg-[#E8F8EF] text-[#178A45]",
                                          },
                                          2: {
                                            label: t("retentionLost"),
                                            className:
                                              "bg-[#FEE2E2] text-[#DC2626]",
                                          },
                                          3: {
                                            label: t("retentionFollowUp"),
                                            className:
                                              "bg-[#FFF4E5] text-[#C2410C]",
                                          },
                                          4: {
                                            label: t("retentionAtRisk"),
                                            className:
                                              "bg-[#FFEDD5] text-[#9A3412]",
                                          },
                                        };
                                        const config =
                                          retentionConfig[retentionStatus];
                                        if (!config) return "—";
                                        return (
                                          <Box
                                            className={`rounded-[50px] w-[fit-content] text-[11px] font-semibold px-[10px] py-[2px] ${config.className}`}
                                          >
                                            {config.label}
                                          </Box>
                                        );
                                      })()
                                    ) : column.id === "clientRetention" ? (
                                      (() => {
                                        const retentionStatus = Number(
                                          row.status ??
                                            row.retentionStatus ??
                                            0,
                                        );
                                        const retentionConfig: Record<
                                          number,
                                          { label: string; className: string }
                                        > = {
                                          1: {
                                            label: t("retentionActive"),
                                            className:
                                              "bg-[#E8F8EF] text-[#178A45]",
                                          },
                                          2: {
                                            label: t("retentionLost"),
                                            className:
                                              "bg-[#FEE2E2] text-[#DC2626]",
                                          },
                                          3: {
                                            label: t("retentionFollowUp"),
                                            className:
                                              "bg-[#FFF4E5] text-[#C2410C]",
                                          },
                                          4: {
                                            label: t("retentionAtRisk"),
                                            className:
                                              "bg-[#FFEDD5] text-[#9A3412]",
                                          },
                                        };
                                        const config =
                                          retentionConfig[retentionStatus];
                                        const days = row.daysSinceLastService;
                                        const hasDays =
                                          days != null && days !== "";
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "4px",
                                              minWidth: 0,
                                              textAlign: "start",
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            {config ? (
                                              <Box
                                                className={`rounded-[50px] w-[fit-content] text-[11px] font-semibold px-[10px] py-[2px] ${config.className}`}
                                              >
                                                {config.label}
                                              </Box>
                                            ) : (
                                              <span className="font-Saira text-[12px] text-[#637381]">
                                                —
                                              </span>
                                            )}
                                            {hasDays ? (
                                              <span className="font-Saira font-[400] text-[11px] text-[#637381]">
                                                {String(days)}{" "}
                                                {t("daysSinceLastServiceShort")}
                                              </span>
                                            ) : null}
                                          </Box>
                                        );
                                      })()
                                    ) : column.id === "daysSinceLastService" ? (
                                      row.daysSinceLastService != null &&
                                      row.daysSinceLastService !== "" ? (
                                        String(row.daysSinceLastService)
                                      ) : (
                                        ""
                                      )
                                    ) : column.id === "userName" ? (
                                      truncDynCell(row["user"]?.name)
                                    ) : column.id === "clientContact" ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "2px",
                                          minWidth: 0,
                                          textAlign: "start",
                                        }}
                                      >
                                        <span className="font-Saira font-[500] text-[12px] text-[#212B36] break-words whitespace-normal">
                                          {row["user"]?.name || "—"}
                                        </span>
                                        <span className="font-Saira font-[400] text-[11px] text-[#637381] break-words whitespace-normal">
                                          {row["user"]?.phone || "—"}
                                        </span>
                                      </Box>
                                    ) : column.id === "userNameEmail" ? (
                                      (() => {
                                        const displayName =
                                          i18n?.language === "ar" &&
                                          loggedInUserData?.business
                                            ?.secondaryLanguage === "Arabic" &&
                                          row?.secondaryName
                                            ? row.secondaryName
                                            : row?.name || "—";
                                        const email =
                                          row?.username || row?.email || "";
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "2px",
                                              minWidth: 0,
                                              textAlign: "start",
                                            }}
                                          >
                                            <span className="font-Saira font-[600] text-[12px] text-[#212B36] break-words whitespace-normal">
                                              {displayName}
                                            </span>
                                            <span className="font-Saira font-[400] text-[11px] text-[#637381] break-words whitespace-normal">
                                              {email || "—"}
                                            </span>
                                          </Box>
                                        );
                                      })()
                                    ) : column.id === "itemNameCode" ||
                                      column.id === "serviceNameCode" ||
                                      column.id === "packageNameCode" ? (
                                      (() => {
                                        const displayName =
                                          i18n?.language === "ar" &&
                                          loggedInUserData?.business
                                            ?.secondaryLanguage === "Arabic" &&
                                          row?.secondaryTitle
                                            ? row.secondaryTitle
                                            : row?.title || "—";
                                        const code =
                                          row?.itemCode ||
                                          row?.serviceCode ||
                                          row?.packageCode ||
                                          "";
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "2px",
                                              minWidth: 0,
                                              textAlign: "start",
                                            }}
                                          >
                                            <span className="font-Saira font-[600] text-[12px] text-[#212B36] break-words whitespace-normal">
                                              {displayName}
                                            </span>
                                            <span className="font-Saira font-[400] text-[11px] text-[#637381] break-words whitespace-normal">
                                              {code || "—"}
                                            </span>
                                          </Box>
                                        );
                                      })()
                                    ) : column.id === "createdAtBy" ? (
                                      (() => {
                                        const createdAtValue =
                                          getIntegrationLogCreatedAt(row) ||
                                          row?.createdAt;
                                        const formattedDate = createdAtValue
                                          ? new Intl.DateTimeFormat(
                                              i18n.language,
                                              {
                                                year: "numeric",
                                                month: "long",
                                                day: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                                second: "2-digit",
                                                numberingSystem:
                                                  i18n.language === "ur" ||
                                                  i18n.language === "ar"
                                                    ? "arab"
                                                    : "latn",
                                              },
                                            ).format(new Date(createdAtValue))
                                          : "—";
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "2px",
                                              minWidth: 0,
                                              textAlign: "start",
                                            }}
                                          >
                                            <span className="font-Saira font-[500] text-[12px] text-[#212B36] break-words whitespace-normal">
                                              {formattedDate}
                                            </span>
                                            <span className="font-Saira font-[400] text-[11px] text-[#637381] break-words whitespace-normal">
                                              {/* {t("createdBy")}:{" "} */}
                                              {row?.createdByName || "—"}
                                            </span>
                                          </Box>
                                        );
                                      })()
                                    ) : column.id === "makeModel" ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "2px",
                                          minWidth: 0,
                                          textAlign: "start",
                                        }}
                                      >
                                        <span className="font-Saira font-[500] text-[12px] text-[#212B36] break-words whitespace-normal">
                                          {row.makeTitle || "—"}
                                        </span>
                                        <span className="font-Saira font-[400] text-[11px] text-[#637381] break-words whitespace-normal">
                                          {row.modelTitle || "—"}
                                        </span>
                                      </Box>
                                    ) : column.id === "clientIdentity" ? (
                                      (() => {
                                        const displayName =
                                          i18n?.language === "ar" &&
                                          loggedInUserData?.business
                                            ?.secondaryLanguage === "Arabic" &&
                                          row?.secondaryName
                                            ? row.secondaryName
                                            : row?.name || "—";
                                        const isCompany = row?.type === 160;
                                        const companyName = isCompany
                                          ? i18n?.language === "ar" &&
                                            loggedInUserData?.business
                                              ?.secondaryLanguage ===
                                              "Arabic" &&
                                            row?.account?.secondaryTitle
                                            ? row.account.secondaryTitle
                                            : row?.account?.title || ""
                                          : "";
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "2px",
                                              minWidth: 0,
                                              textAlign: "start",
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            <span className="font-Saira font-[600] text-[12px] text-[#212B36] break-words whitespace-normal">
                                              {displayName}
                                            </span>
                                            <span className="font-Saira font-[400] text-[11px] text-[#637381] break-words whitespace-normal">
                                              {companyName || "—"}
                                            </span>
                                          </Box>
                                        );
                                      })()
                                    ) : column.id === "clientTypeCompany" ? (
                                      (() => {
                                        const isCompany = row?.type === 160;
                                        if (!isCompany) return "—";
                                        const companyName =
                                          i18n?.language === "ar" &&
                                          loggedInUserData?.business
                                            ?.secondaryLanguage === "Arabic" &&
                                          row["account"]?.secondaryTitle
                                            ? row["account"]?.secondaryTitle
                                            : row["account"]?.title;
                                        return (
                                          <span className="font-Saira font-[500] text-[12px] text-[#212B36] break-words whitespace-normal">
                                            {companyName || "—"}
                                          </span>
                                        );
                                      })()
                                    ) : column.id === "clientTypeSource" ? (
                                      (() => {
                                        const typeLabel = [
                                          {
                                            value: 170,
                                            label: t("Individual"),
                                          },
                                          {
                                            value: 180,
                                            label: t("Individual"),
                                          },
                                          {
                                            value: 160,
                                            label: t("company"),
                                          },
                                        ].find(
                                          (option) =>
                                            option.value === row?.type,
                                        )?.label;
                                        const sourceLabel = [
                                          { label: t("Walk-in"), value: 1 },
                                          {
                                            label: t("Phone / WhatsApp"),
                                            value: 2,
                                          },
                                          { label: t("Online"), value: 3 },
                                          { label: t("Referral"), value: 4 },
                                          { label: t("Insurance"), value: 5 },
                                          {
                                            label: t("Corporate / Fleet"),
                                            value: 6,
                                          },
                                          {
                                            label: t("Returning Customer"),
                                            value: 7,
                                          },
                                          { label: t("OTHER"), value: 8 },
                                        ].find(
                                          (option) =>
                                            option.value ===
                                            Number(row?.source),
                                        )?.label;
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "2px",
                                              minWidth: 0,
                                              textAlign: "start",
                                            }}
                                          >
                                            <span className="font-Saira font-[400] text-[11px] text-[#637381] break-words whitespace-normal">
                                              {sourceLabel || "—"}
                                            </span>
                                            <span className="font-Saira font-[500] text-[12px] text-[#212B36] break-words whitespace-normal">
                                              {typeLabel || "—"}
                                            </span>
                                          </Box>
                                        );
                                      })()
                                    ) : column.id === "contact" ? (
                                      (() => {
                                        const email =
                                          row.autoDetails === 2 ||
                                          row.autoDetails === 10 ||
                                          row.autoDetails === 11 ||
                                          row.autoDetails === 12 ||
                                          row.autoDetails === 20 ||
                                          row.autoDetails === 21
                                            ? row?.account?.email || ""
                                            : row.autoDetails === 0 ||
                                                row.autoDetails === 1
                                              ? ""
                                              : row?.username ||
                                                row?.account?.email ||
                                                row?.email ||
                                                "";
                                        const phone =
                                          row?.phone ||
                                          row?.phoneNumber1 ||
                                          row?.account?.phone ||
                                          "";
                                        return (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexDirection: "column",
                                              gap: "2px",
                                              minWidth: 0,
                                              textAlign: "start",
                                            }}
                                          >
                                            <span className="font-Saira font-[500] text-[12px] text-[#212B36] break-words whitespace-normal">
                                              {email || "—"}
                                            </span>
                                            <span className="font-Saira font-[400] text-[11px] text-[#637381] break-words whitespace-normal">
                                              {phone || "—"}
                                            </span>
                                          </Box>
                                        );
                                      })()
                                    ) : column.id === "cityCountry" ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "2px",
                                          minWidth: 0,
                                          textAlign: "start",
                                        }}
                                      >
                                        <span className="font-Saira font-[500] text-[12px] text-[#212B36] break-words whitespace-normal">
                                          {row?.city || "—"}
                                        </span>
                                        <span className="font-Saira font-[400] text-[11px] text-[#637381] break-words whitespace-normal">
                                          {row?.country || "—"}
                                        </span>
                                      </Box>
                                    ) : column.id ===
                                      "userNameHostingDetails" ? (
                                      truncDynCell(row?.userName)
                                    ) : column.id === "userNameClientGroup" ||
                                      column.id === "userNameClient" ? (
                                      row.autoDetails === 2 ||
                                      row.autoDetails === 10 ||
                                      row.autoDetails === 11 ||
                                      row.autoDetails === 12 ||
                                      row.autoDetails === 20 ||
                                      row.autoDetails === 21 ? (
                                        truncDynCell(row?.account?.email || "")
                                      ) : (
                                        truncDynCell(
                                          row.autoDetails === 0 ||
                                            row.autoDetails === 1
                                            ? ""
                                            : row?.username ||
                                                row?.account?.email ||
                                                "",
                                        )
                                      )
                                    ) : column.id === "userPhone" ? (
                                      row["user"]?.phone
                                    ) : column.id === "haveExpiry" ||
                                      column.id === "default" ||
                                      column.id === "systemDefault" ||
                                      column.id === "isDefault" ? (
                                      row[column.id] ? (
                                        t("yes")
                                      ) : (
                                        t("no")
                                      )
                                    ) : column.id === "pricingScope" ? (
                                      row[column.id] === 2 ? (
                                        t("Item & Service")
                                      ) : row[column.id] === 1 ? (
                                        t("Service only")
                                      ) : (
                                        ""
                                      )
                                    ) : column.id === "pricingType" ? (
                                      row[column.id] === 2 ? (
                                        t("standardLaborCost")
                                      ) : row[column.id] === 1 ? (
                                        t("fixedPrice")
                                      ) : (
                                        ""
                                      )
                                    ) : column.id === "pricingTypePackages" ? (
                                      row["pricingType"] === 1 ? (
                                        t("fixed")
                                      ) : row["pricingType"] === 2 ? (
                                        t("serviceSum")
                                      ) : (
                                        ""
                                      )
                                    ) : column.id === "itemGroup" ? (
                                      row[column.id] === 1 ? (
                                        t("group")
                                      ) : row[column.id] === 2 ? (
                                        t("open")
                                      ) : row[column.id] === 3 ? (
                                        t("selected")
                                      ) : row[column.id] === 4 ? (
                                        t("bundled")
                                      ) : (
                                        ""
                                      )
                                    ) : column.id === "stockType" ? (
                                      row[column.id] === 4 ? (
                                        t("Stock First")
                                      ) : row[column.id] === 5 ? (
                                        t("Invoice First")
                                      ) : (
                                        ""
                                      )
                                    ) : column.id === "urgencyStatus" ? (
                                      row[column.id] ? (
                                        <Box
                                          className={`${
                                            row[column.id] === 10
                                              ? "text-green-500 status-active"
                                              : row[column.id] === 20
                                                ? "text-orange-500 status-inactive"
                                                : row[column.id] === 30
                                                  ? "text-red-500 status-cancelled"
                                                  : ""
                                          } 
                                        font-medium text-[13px] text-center px-3 py-1 rounded-lg
                                    `}
                                        >
                                          {row[column.id] === 10
                                            ? t("Low")
                                            : row[column.id] === 20
                                              ? t("Medium")
                                              : row[column.id] === 30
                                                ? t("High")
                                                : ""}
                                        </Box>
                                      ) : (
                                        ""
                                      )
                                    ) : column.id === "bayStatus" ? (
                                      <Box
                                        className={
                                          (row?.status === 0 ||
                                          row?.status === 1
                                            ? "status-active"
                                            : row?.status === 2
                                              ? "status-inactive"
                                              : "") +
                                          " px-3 py-1 rounded-lg w-[fit-content]"
                                        }
                                      >
                                        {row["status"] === 0
                                          ? t("available")
                                          : row["status"] === 1
                                            ? t("available")
                                            : row["status"] === 2
                                              ? t("occupied")
                                              : ""}
                                      </Box>
                                    ) : column.id === "integrationModule" ? (
                                      t(integrationModuleLabel(row["module"]))
                                    ) : column.id === "integrationAction" ? (
                                      t(integrationActionLabel(row["action"]))
                                    ) : column.id === "integrationStatus" ? (
                                      <Tooltip
                                        title={row?.errorMessage || ""}
                                        arrow
                                        placement="top"
                                        enterDelay={0}
                                        disableHoverListener={
                                          !row?.errorMessage
                                        }
                                      >
                                        <Box className="flex justify-center items-center w-full">
                                          <Box
                                            component="span"
                                            className={
                                              (getIntegrationLogStatus(row) ===
                                              1
                                                ? "status-active"
                                                : getIntegrationLogStatus(
                                                      row,
                                                    ) === 2
                                                  ? "status-cancelled"
                                                  : getIntegrationLogStatus(
                                                        row,
                                                      ) === 0
                                                    ? "status-inactive"
                                                    : "") +
                                              " inline-flex items-center px-2 py-0.5 rounded-full w-fit"
                                            }
                                          >
                                            <Typography className="text-[11px] leading-none font-medium">
                                              {getIntegrationLogStatus(row) ===
                                              1
                                                ? t("SUCCESS")
                                                : getIntegrationLogStatus(
                                                      row,
                                                    ) === 2
                                                  ? t("FAILED")
                                                  : getIntegrationLogStatus(
                                                        row,
                                                      ) === 0
                                                    ? t("PENDING")
                                                    : ""}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </Tooltip>
                                    ) : column.id === "integrationRetry" ? (
                                      shouldShowIntegrationRetry(row, data) &&
                                      onIntegrationLogRetry ? (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={
                                            integrationRetryLoadingId ===
                                            row?.id
                                          }
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            onIntegrationLogRetry(row);
                                          }}
                                          className="font-Saira normal-case"
                                          sx={{
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: 12,
                                          }}
                                        >
                                          {integrationRetryLoadingId ===
                                          row?.id ? (
                                            <CircularProgress size={18} />
                                          ) : (
                                            t("retry")
                                          )}
                                        </Button>
                                      ) : null
                                    ) : column.id === "integrationVin" ? (
                                      row?.vin || ""
                                    ) : column.id === "integrationJobId" ? (
                                      (row?.jobId ??
                                      row?.requestPayload?.jobId ??
                                      "")
                                    ) : column.id ===
                                      "integrationReferenceNo" ? (
                                      getIntegrationReferenceNumber(row) || ""
                                    ) : column.id === "direction" ? (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexWrap: "wrap",
                                          alignItems: "center",
                                          gap: 0.5,
                                          maxWidth: "100%",
                                          minWidth: 0,
                                          wordBreak: "break-word",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {formatIntegrationDirectionLabel(
                                          row?.direction?.from,
                                        )}
                                        <EastIcon
                                          sx={{
                                            fontSize: 17,
                                            flexShrink: 0,
                                            transform: isRTL
                                              ? "scaleX(-1)"
                                              : "none",
                                          }}
                                        />
                                        {formatIntegrationDirectionLabel(
                                          row?.direction?.to,
                                        )}
                                      </Box>
                                    ) : column.id === "status" ? (
                                      row[column.id] === 1 ? (
                                        t("draft")
                                      ) : row[column.id] === 2 ? (
                                        row?.received === true ? (
                                          t("Received")
                                        ) : row?.flowStatus === 1 ? (
                                          t("billed")
                                        ) : (
                                          t("approved")
                                        )
                                      ) : row[column.id] === 3 ? (
                                        t("Received")
                                      ) : row[column.id] === 4 ? (
                                        t("partialReceived")
                                      ) : (
                                        ""
                                      )
                                    ) : column.id === "accountTitleClient" ? (
                                      i18n?.language === "ar" &&
                                      loggedInUserData?.business
                                        ?.secondaryLanguage === "Arabic" &&
                                      row["account"]?.secondaryTitle ? (
                                        truncDynCell(
                                          row["account"]?.secondaryTitle,
                                        )
                                      ) : (
                                        truncDynCell(row["account"]?.title)
                                      )
                                    ) : column.id === "typeLabel" ? (
                                      truncDynCell(row.type)
                                    ) : column.id === "stockLevel" ? (
                                      truncDynCell(row?.baseItemQuantity || "")
                                    ) : column.id === "subscriptionPlan" ||
                                      column.id === "subscriptionPlanTitle" ? (
                                      truncDynCell(
                                        row?.stationPlan?.planTitle ||
                                          row?.businessPlan?.planTitle ||
                                          "",
                                        column.maxWidth,
                                      )
                                    ) : column.id === "checklistitemtype" ? (
                                      row.type === "select" ? (
                                        t("dropdown")
                                      ) : (
                                        truncDynCell(row.type)
                                      )
                                    ) : column.id === "type" ? (
                                      [
                                        { value: 100, label: t("Reseller") },
                                        {
                                          value: 110,
                                          label: t("Maintenance Head"),
                                        },
                                        {
                                          value: 120,
                                          label: t("Admin"),
                                        },
                                        {
                                          value: 130,
                                          label: t("Station Manager"),
                                        },
                                        {
                                          value: 140,
                                          label: t("Service Manager"),
                                        },
                                        { value: 150, label: t("Technician") },
                                        { value: 200, label: t("Supervisor") },
                                        {
                                          value: 210,
                                          label: t("Inventory Manager"),
                                        },
                                        {
                                          value: 220,
                                          label: t("Account Manager"),
                                        },
                                        {
                                          value: 230,
                                          label: t("Finance Controller"),
                                        },
                                        {
                                          value: 240,
                                          label: t("Technical Support"),
                                        },
                                      ].find(
                                        (option: any) =>
                                          option.value === row[column.id],
                                      )?.label
                                    ) : column.id === "source" ? (
                                      [
                                        { label: t("Walk-in"), value: 1 },
                                        {
                                          label: t("Phone / WhatsApp"),
                                          value: 2,
                                        },
                                        { label: t("Online"), value: 3 },
                                        { label: t("Referral"), value: 4 },
                                        { label: t("Insurance"), value: 5 },
                                        {
                                          label: t("Corporate / Fleet"),
                                          value: 6,
                                        },
                                        {
                                          label: t("Returning Customer"),
                                          value: 7,
                                        },
                                        { label: t("OTHER"), value: 8 },
                                      ].find(
                                        (option: any) =>
                                          option.value ===
                                          Number(row[column.id]),
                                      )?.label
                                    ) : column.id === "businesstemplateType" ? (
                                      [
                                        { value: 2, label: t("invoice") },
                                        { value: 3, label: t("jobCard") },
                                        { value: 4, label: t("workOrder") },
                                        { value: 5, label: t("inspection") },
                                        { value: 6, label: t("deliveryNote") },
                                        {
                                          value: 7,
                                          label: t("proformaInvoice"),
                                        },
                                      ].find(
                                        (option: any) =>
                                          option.value === row["type"],
                                      )?.label
                                    ) : column.id === "designVariant" ? (
                                      (() => {
                                        const variant =
                                          row?.designVariant ??
                                          row?.attributes?.designVariant ??
                                          row?.settings?.designVariant;
                                        const isModern =
                                          variant === 2 ||
                                          variant === "2" ||
                                          variant === "modern";
                                        if (isModern) {
                                          return (
                                            <Box className="rounded-[50px] w-fit text-[11px] font-semibold px-[10px] py-[3px] bg-[#C4C5E8] text-[#0007B4]">
                                              {t("modernDesign")}
                                            </Box>
                                          );
                                        } else {
                                          return (
                                            <Box className="rounded-[50px] w-fit text-[11px] font-semibold px-[10px] py-[3px] bg-[#E8E4F0] text-[#5B5675]">
                                              {t("classicDesign")}
                                            </Box>
                                          );
                                        }
                                      })()
                                    ) : column.id === "adjustmentType" ? (
                                      [
                                        { value: 590, label: t("Advance") },
                                        { value: 591, label: t("FIFO") },
                                        { value: 592, label: t("MANUAL") },
                                      ].find(
                                        (option: any) =>
                                          option.value === row[column.id],
                                      )?.label
                                    ) : column.id === "stockAdjustmentType" ? (
                                      [
                                        { value: 1, label: t("remove") },
                                        { value: 2, label: t("add") },
                                      ].find(
                                        (option: any) =>
                                          option.value ===
                                          row["adjustmentType"],
                                      )?.label
                                    ) : column.id === "clientType" ? (
                                      [
                                        { value: 170, label: t("Individual") },
                                        { value: 160, label: t("company") },
                                        { value: 180, label: t("Individual") },
                                      ].find(
                                        (option: any) =>
                                          option.value === row["type"],
                                      )?.label
                                    ) : column.id === "accountType" ? (
                                      [
                                        { value: 10, label: t("cash") },
                                        { value: 20, label: t("bank") },
                                      ].find(
                                        (option: any) =>
                                          option.value === row["type"],
                                      )?.label
                                    ) : column.id === "paymentModeTitle" ? (
                                      truncDynCell(
                                        row?.paymentModeTitle || "",
                                        column.maxWidth,
                                      )
                                    ) : column.id === "invoiceType" ? (
                                      [
                                        { value: 1, label: t("Purchase") },
                                        { value: 2, label: t("expense") },
                                      ].find(
                                        (option: any) =>
                                          option.value === row["invoiceType"],
                                      )?.label
                                    ) : column.id === "billingCycle" ? (
                                      [
                                        { value: 1, label: t("monthly") },
                                        { value: 2, label: t("yearly") },
                                        { value: 3, label: t("weekly") },
                                      ].find(
                                        (option: any) =>
                                          option.value === row["billingCycle"],
                                      )?.label
                                    ) : column.id === "category" ? (
                                      [
                                        { value: 1, label: t("fixedAsset") },
                                        { value: 2, label: t("inventoryItem") },
                                        {
                                          value: 3,
                                          label: t("spotPurchaseItem"),
                                        },
                                      ].find(
                                        (option: any) =>
                                          option.value === row["category"],
                                      )?.label
                                    ) : column.id === "categoryService" ? (
                                      [
                                        { value: 1, label: t("internal") },
                                        { value: 4, label: t("external") },
                                      ].find(
                                        (option: any) =>
                                          option.value === row["type"],
                                      )?.label
                                    ) : column.id === "recommendationType" ? (
                                      [
                                        { label: t("package"), value: 1 },
                                        { label: t("service"), value: 2 },
                                        { label: t("part"), value: 3 },
                                      ].find(
                                        (option: any) =>
                                          option.value === row["type"],
                                      )?.label
                                    ) : column.id === "integrationType" ? (
                                      <Box
                                        className={
                                          (row["type"] === 1
                                            ? "status-active"
                                            : row["type"] === 3
                                              ? "bg-[#e31b5a17]"
                                              : row["type"] === 4
                                                ? "bg-[#1b6be317]"
                                                : "status-inactive") +
                                          " px-3 " +
                                          (row["type"] === 3 ||
                                          row["type"] === 4
                                            ? "py-[6px]"
                                            : "py-1") +
                                          " rounded-lg w-[130px] flex gap-[6px] items-center justify-center"
                                        }
                                        style={{
                                          color:
                                            row["type"] === 2
                                              ? themeColors?.primary
                                              : row["type"] === 3
                                                ? "#E31B5C"
                                                : row["type"] === 4
                                                  ? "#1B6BE3"
                                                  : "",
                                        }}
                                      >
                                        {row["type"] === 1 ? (
                                          <WhatsAppIcon className="w-[20px]" />
                                        ) : row["type"] === 2 ? (
                                          <PaymentIcon
                                            className="w-[20px]"
                                            style={{
                                              fill: themeColors?.primary,
                                            }}
                                          />
                                        ) : row["type"] === 3 ? (
                                          <img
                                            src={tronstrideIcon}
                                            alt=""
                                            className="w-[22px]"
                                          />
                                        ) : row["type"] === 4 ? (
                                          <DirectionsCarIcon
                                            className="w-[20px]"
                                            style={{ fill: "#1B6BE3" }}
                                          />
                                        ) : (
                                          ""
                                        )}
                                        <span>
                                          {row["type"] === 1
                                            ? t("whatsapp")
                                            : row["type"] === 2
                                              ? t("payment")
                                              : row["type"] === 3
                                                ? t("Tronstride")
                                                : row["type"] === 4
                                                  ? t("FleetOS")
                                                  : ""}
                                        </span>
                                      </Box>
                                    ) : column.id === "paymentStatus" ? (
                                      <span
                                        className={`${row?.paymentStatus === 777 ? "unpaid" : row?.paymentStatus === 778 ? "partialpaid" : "paid"} p-1 rounded-lg block mx-auto text-center max-w-[100px]`}
                                      >
                                        {row?.paymentStatus === 777
                                          ? t("unpaid")
                                          : row?.paymentStatus === 778
                                            ? t("partialPaid")
                                            : t("paid")}
                                      </span>
                                    ) : column.id ===
                                      "accountContractNumber" ? (
                                      truncDynCell(
                                        row["account"]?.contractNumber,
                                      )
                                    ) : column.id === "itemCost" ? (
                                      <>
                                        {new Intl.NumberFormat("en", {
                                          notation: "standard",
                                          maximumFractionDigits: 2,
                                        }).format(row[column.id] || 0)}
                                        <span className="uppercase ms-[3px]">
                                          {currency}
                                        </span>
                                      </>
                                    ) : column.id === "amountAccount" ? (
                                      <>
                                        <span
                                          className="font-[600]"
                                          style={{
                                            color: themeColors?.primary,
                                          }}
                                        >
                                          {new Intl.NumberFormat("en", {
                                            notation: "standard",
                                            maximumFractionDigits: 2,
                                          }).format(row["amount"] || 0)}
                                        </span>
                                        <span className="uppercase ms-[3px] font-[400] text-[11px]">
                                          {currency}
                                        </span>
                                      </>
                                    ) : column.id === "stationName" ? (
                                      truncDynCell(
                                        row["attributes"]?.stationName ||
                                          row["stationName"],
                                      )
                                    ) : column.id === "clientAttributes" ? (
                                      truncDynCell(
                                        row["attributes"]?.clientName ||
                                          row["attributes"]?.customerName,
                                      )
                                    ) : column.id === "invoiceTemplateName" ? (
                                      truncDynCell(t(row?.name))
                                    ) : column.id === "creditDebit" ? (
                                      <Box className="flex gap-1 items-center">
                                        {row["credit"] ? (
                                          <ArrowUp
                                            style={{
                                              fill: "#17AE36",
                                              width: "14px",
                                              height: "14px",
                                            }}
                                          />
                                        ) : row["debit"] ? (
                                          <ArrowDown
                                            style={{
                                              fill: "red",
                                              width: "14px",
                                              height: "14px",
                                            }}
                                          />
                                        ) : (
                                          ""
                                        )}
                                        <span
                                          className={`${row["credit"] ? "text-[#17AE36]" : "text-[#FF0000]"}`}
                                        >
                                          {row["credit"] || row["debit"]
                                            ? new Intl.NumberFormat("en", {
                                                notation: "standard",
                                                maximumFractionDigits: 2,
                                              }).format(
                                                row["credit"] || row["debit"],
                                              )
                                            : null}
                                        </span>
                                        {row["credit"] || row["debit"] ? (
                                          <span
                                            className={`uppercase mt-[1px] font-[400] text-[11px] ${row["credit"] ? "text-[#17AE36]" : "text-[#FF0000]"}`}
                                          >
                                            {currency}
                                          </span>
                                        ) : (
                                          ""
                                        )}
                                      </Box>
                                    ) : column.id === "currentBalance" ||
                                      column.id === "balance" ? (
                                      <Box
                                        padding={"0 10px"}
                                        // onClick={(event) => {
                                        //   event.stopPropagation();
                                        //   navigate(`/account/${row.accountTitle}/${row.id}/ledger`);
                                        // }}
                                        sx={{ color: themeColors?.primary }}
                                      >
                                        <span className="font-[600]">
                                          {new Intl.NumberFormat("en", {
                                            notation: "standard",
                                            maximumFractionDigits: 2,
                                          }).format(row[column.id] || 0)}
                                        </span>
                                        <span className="uppercase ms-[3px] font-[400] text-[11px]">
                                          {currency}
                                        </span>
                                      </Box>
                                    ) : column.id === "favorite" ? (
                                      <Box
                                        className="w-[100%] text-center"
                                        onClick={(
                                          event: React.MouseEvent<
                                            HTMLDivElement,
                                            MouseEvent
                                          >,
                                        ) => {
                                          event.stopPropagation();
                                          markFavourite?.(row);
                                        }}
                                      >
                                        {row[column.id] ? (
                                          <StarIcon
                                            sx={{
                                              fill: themeColors?.secondary,
                                            }}
                                          />
                                        ) : (
                                          <StarOutlineIcon
                                            sx={{
                                              fill: themeColors?.secondary,
                                            }}
                                          />
                                        )}
                                      </Box>
                                    ) : column.id === "active" ? (
                                      row[column.id] ? (
                                        <Box
                                          onClick={() => {
                                            (!displayDefault && row?.default) ||
                                            row?.systemDefault ||
                                            row.isDefault
                                              ? null
                                              : onChangeStatus?.(row);
                                          }}
                                          className={`text-green-500 cursor-pointer font-medium`}
                                          sx={{
                                            whiteSpace: "nowrap",
                                            "&:hover": {
                                              color: `${themeColors?.primary} !important`,
                                            },
                                          }}
                                        >
                                          {(!displayDefault && row?.default) ||
                                          row?.systemDefault ||
                                          row.isDefault
                                            ? ""
                                            : t("active")}
                                        </Box>
                                      ) : (
                                        <Box
                                          onClick={() => {
                                            (!displayDefault && row?.default) ||
                                            row?.systemDefault ||
                                            row.isDefault
                                              ? null
                                              : onChangeStatus?.(row);
                                          }}
                                          className={`text-red-500 cursor-pointer font-medium`}
                                          sx={{
                                            whiteSpace: "nowrap",
                                            "&:hover": {
                                              color: `${themeColors?.primary} !important`,
                                            },
                                          }}
                                        >
                                          {t("inactive")}
                                        </Box>
                                      )
                                    ) : column.id === "bookingStatus" ? (
                                      <Box
                                        className={`${row?.status === 1 ? "unpaid" : row?.status === 2 ? "status-active" : "status-cancelled"} px-3 py-1 rounded-lg text-center w-[100px]`}
                                      >
                                        {row?.status === 1
                                          ? t("pending")
                                          : row?.status === 2
                                            ? t("approved")
                                            : t("rejected")}
                                      </Box>
                                    ) : column.id === "loginAsUser" ? (
                                      <Box
                                        onClick={(event) =>
                                          event.stopPropagation()
                                        }
                                        sx={{
                                          width: "100%",
                                          minWidth: 0,
                                          display: "flex",
                                          flexDirection: "row",
                                          gap: 1,
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 0.75,
                                          }}
                                        >
                                          <Controller
                                            name={`users-${row.id}`}
                                            control={control}
                                            defaultValue={null}
                                            rules={{
                                              required: t(
                                                "This field is required.",
                                              ),
                                            }}
                                            render={({ field }) => (
                                              <Box
                                                sx={{
                                                  width: "100%",
                                                  minWidth: 0,
                                                  "& .commonSelect": {
                                                    width: "100%",
                                                  },
                                                  "& .commonSelect .dropdown": {
                                                    display: "none",
                                                  },
                                                  "& .commonSelect .css-7896aj-control, & .commonSelect .css-1xiok1u-control, & .commonSelect [class*='-control']":
                                                    {
                                                      minHeight: "32px",
                                                      minWidth: 0,
                                                    },
                                                  "& .commonSelect [class*='-singleValue']":
                                                    {
                                                      maxWidth: "100%",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap",
                                                      position: "static",
                                                      transform: "none",
                                                      margin: 0,
                                                    },
                                                  "& .commonSelect [class*='-ValueContainer']":
                                                    {
                                                      flexWrap: "nowrap",
                                                      overflow: "hidden",
                                                      padding: "0 6px",
                                                    },
                                                }}
                                              >
                                                <DynamicSelect
                                                  label={""}
                                                  {...field}
                                                  options={loginAsUserOptions}
                                                  isClearable
                                                  isSearchable={false}
                                                  ddWidth="28px"
                                                  ddHeight="28px"
                                                  onChange={(
                                                    selectedOption: any,
                                                  ) => {
                                                    visibleRows.forEach(
                                                      (r: any) => {
                                                        if (r?.id !== row?.id) {
                                                          setValue(
                                                            `users-${r.id}`,
                                                            null,
                                                          );
                                                          setValue(
                                                            `stationId-${r.id}`,
                                                            null,
                                                          );
                                                        }
                                                      },
                                                    );
                                                    field.onChange(
                                                      selectedOption,
                                                    );
                                                    setValue(
                                                      `stationId-${row.id}`,
                                                      null,
                                                    );
                                                    if (
                                                      selectedOption?.value ===
                                                      `${checkUrl("POS")}/#`
                                                    ) {
                                                      setStationDropdownBusinessId(
                                                        row?.businessId ||
                                                          undefined,
                                                      );
                                                      setValue(
                                                        "__activePosRowId",
                                                        row.id,
                                                      );
                                                      handleDropdownActions(
                                                        "searchIt",
                                                        "",
                                                      );
                                                    } else {
                                                      setStationDropdownBusinessId(
                                                        undefined,
                                                      );
                                                      if (
                                                        watch(
                                                          "__activePosRowId",
                                                        ) === row.id
                                                      )
                                                        setValue(
                                                          "__activePosRowId",
                                                          null,
                                                        );
                                                    }
                                                    if (selectedOption?.value)
                                                      trigger([
                                                        `users-${row.id}`,
                                                      ]);
                                                  }}
                                                  error={
                                                    errors[`users-${row.id}`]
                                                      ?.message as string
                                                  }
                                                />
                                              </Box>
                                            )}
                                          />
                                          {watch(`users-${row.id}`)?.value ===
                                            `${checkUrl("POS")}/#` &&
                                          watch("__activePosRowId") ===
                                            row.id ? (
                                            <Controller
                                              name={`stationId-${row.id}`}
                                              control={control}
                                              rules={{
                                                required: t(
                                                  "This field is required.",
                                                ),
                                              }}
                                              defaultValue={null}
                                              render={({ field }) => (
                                                <Box
                                                  sx={{
                                                    width: "100%",
                                                    minWidth: 0,
                                                    "& .commonSelect": {
                                                      width: "100%",
                                                    },
                                                    "& .commonSelect .dropdown":
                                                      {
                                                        display: "none",
                                                      },
                                                    "& .commonSelect .css-7896aj-control, & .commonSelect .css-1xiok1u-control, & .commonSelect [class*='-control']":
                                                      {
                                                        minHeight: "32px",
                                                        minWidth: 0,
                                                      },
                                                    "& .commonSelect [class*='-singleValue']":
                                                      {
                                                        maxWidth: "100%",
                                                        overflow: "hidden",
                                                        textOverflow:
                                                          "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        position: "static",
                                                        transform: "none",
                                                        margin: 0,
                                                      },
                                                    "& .commonSelect [class*='-ValueContainer']":
                                                      {
                                                        flexWrap: "nowrap",
                                                        overflow: "hidden",
                                                        padding: "0 6px",
                                                      },
                                                  }}
                                                >
                                                  <DynamicSelect
                                                    label={""}
                                                    {...field}
                                                    options={stations}
                                                    isClearable
                                                    isSearchable
                                                    ddWidth="28px"
                                                    ddHeight="28px"
                                                    onChange={(
                                                      selectedOption: any,
                                                    ) =>
                                                      field.onChange(
                                                        selectedOption,
                                                      )
                                                    }
                                                    error={
                                                      errors[
                                                        `stationId-${row.id}`
                                                      ]?.message as string
                                                    }
                                                    defaultPlaceholder={t(
                                                      "Station",
                                                    )}
                                                    isLoading={
                                                      searchLoading ===
                                                      "stations"
                                                    }
                                                    onInputChange={(
                                                      search: string,
                                                      {
                                                        action,
                                                      }: { action: string },
                                                    ) => {
                                                      if (
                                                        action ===
                                                          "input-change" ||
                                                        (action !==
                                                          "set-value" &&
                                                          action !==
                                                            "menu-close" &&
                                                          action !==
                                                            "input-blur")
                                                      )
                                                        handleDropdownActions(
                                                          "searchIt",
                                                          search,
                                                        );
                                                      else if (
                                                        action ===
                                                          "input-blur" ||
                                                        action === "menu-close"
                                                      ) {
                                                        if (
                                                          dropdownSearchTerm ||
                                                          dropdownPageNo > 1
                                                        )
                                                          handleDropdownActions(
                                                            "searchIt",
                                                            "",
                                                          );
                                                        else
                                                          setDropdownSearchTerm(
                                                            "",
                                                          );
                                                        setDropdownPageNo(1);
                                                      }
                                                    }}
                                                    onMenuScrollToBottom={() => {
                                                      if (
                                                        !(
                                                          stationsData?.pages[0]
                                                            ?.data as any
                                                        )?.last
                                                      )
                                                        handleDropdownActions(
                                                          "loadNext",
                                                        );
                                                    }}
                                                    page={dropdownPageNo}
                                                  />
                                                </Box>
                                              )}
                                            />
                                          ) : null}
                                        </Box>
                                        <LoginIcon
                                          onClick={async () => {
                                            const isValid = await trigger([
                                              `users-${row.id}`,
                                              `stationId-${row.id}`,
                                            ]);
                                            if (isValid) {
                                              if (row?.active === false) {
                                                enqueueSnackbar(
                                                  <AlertMessages
                                                    text={t(
                                                      "loginAsUserInactiveError",
                                                    )}
                                                  />,
                                                  { variant: "error" },
                                                );
                                                return;
                                              }
                                              getUserById(
                                                row.id,
                                                watch(`users-${row.id}`)?.value,
                                                watch(`stationId-${row.id}`)
                                                  ?.value,
                                              );
                                            }
                                          }}
                                          className="cursor-pointer"
                                          sx={{
                                            color: themeColors?.primary,
                                            flexShrink: 0,
                                            fontSize: 21,
                                            // alignSelf: "center"
                                            marginBottom: "8px",
                                          }}
                                        />
                                      </Box>
                                    ) : column.id === "inventoryMode" ? (
                                      (() => {
                                        const mode =
                                          getStoreInventoryModeValue(row);
                                        if (!mode) return "";
                                        const isBin = mode === 2;
                                        return (
                                          <Box
                                            component="span"
                                            sx={{
                                              display: "inline-block",
                                              fontSize: "10px",
                                              fontWeight: 700,
                                              letterSpacing: "0.03em",
                                              px: 1.25,
                                              py: 0.35,
                                              borderRadius: "12px",
                                              color: isBin
                                                ? "#B7791F"
                                                : "#0F7A6B",
                                              bgcolor: isBin
                                                ? "#FBF0DD"
                                                : "#E4F2F0",
                                            }}
                                          >
                                            {isBin ? "BIN" : "OPEN"}
                                          </Box>
                                        );
                                      })()
                                    ) : column.id === "stationsForStore" ? (
                                      truncDynCell(
                                        (i18n?.language === "ar" ||
                                          i18n?.language === "ur") &&
                                          row?.station?.[0]
                                            ?.stationSecondaryName
                                          ? row.station[0].stationSecondaryName
                                          : row?.station?.[0]?.stationName ||
                                              "",
                                      )
                                    ) : column.id === "technicians" ? (
                                      truncDynCell(
                                        Array.isArray(row[column.id])
                                          ? row[column.id][0]?.name || ""
                                          : row[column.id] || "",
                                      )
                                    ) : column.id === "rating" ? (
                                      <Box className="flex gap-1 justify-left items-center">
                                        <Rating
                                          name={`client-rating-${row?.id ?? column.id}`}
                                          value={
                                            row?.rating != null &&
                                            !Number.isNaN(Number(row.rating))
                                              ? Number(row.rating)
                                              : 0
                                          }
                                          precision={0.1}
                                          max={5}
                                          readOnly
                                          icon={
                                            <StarIcon
                                              fontSize="inherit"
                                              sx={{
                                                color:
                                                  themeColors?.secondary ||
                                                  "#E67E22",
                                              }}
                                            />
                                          }
                                          emptyIcon={
                                            <StarOutlineIcon
                                              fontSize="inherit"
                                              sx={{
                                                color:
                                                  themeColors?.secondary ||
                                                  "#E67E22",
                                              }}
                                            />
                                          }
                                          sx={{
                                            fontSize: "17px",
                                            "& .MuiRating-iconFilled": {
                                              color:
                                                themeColors?.secondary ||
                                                "#E67E22",
                                            },
                                            "& .MuiRating-iconEmpty": {
                                              color:
                                                themeColors?.secondary ||
                                                "#E67E22",
                                            },
                                          }}
                                        />
                                      </Box>
                                    ) : // Titles change on Arabic
                                    column.id === "plateNumber" ? (
                                      i18n?.language === "ar" &&
                                      loggedInUserData?.business
                                        ?.secondaryLanguage === "Arabic" &&
                                      row?.secondaryPlateNumber ? (
                                        truncDynCell(row?.secondaryPlateNumber)
                                      ) : (
                                        truncDynCell(row?.plateNumber)
                                      )
                                    ) : column.id === "title" ? (
                                      i18n?.language === "ar" &&
                                      loggedInUserData?.business
                                        ?.secondaryLanguage === "Arabic" &&
                                      row?.secondaryTitle ? (
                                        truncDynCell(row?.secondaryTitle)
                                      ) : (
                                        truncDynCell(row?.title)
                                      )
                                    ) : column.id === "id" ? (
                                      row[column.id]
                                    ) : column.id === "name" ? (
                                      i18n?.language === "ar" &&
                                      loggedInUserData?.business
                                        ?.secondaryLanguage === "Arabic" &&
                                      row?.secondaryName ? (
                                        truncDynCell(
                                          row?.secondaryName,
                                          column.maxWidth,
                                        )
                                      ) : (
                                        truncDynCell(row?.name, column.maxWidth)
                                      )
                                    ) : column.id === "accountTitle" ? (
                                      i18n?.language === "ar" &&
                                      loggedInUserData?.business
                                        ?.secondaryLanguage === "Arabic" &&
                                      row?.secondaryAccountTitle ? (
                                        truncDynCell(row?.secondaryAccountTitle)
                                      ) : (
                                        truncDynCell(row?.accountTitle)
                                      )
                                    ) : column.id === "unitTitle" ? (
                                      i18n?.language === "ar" &&
                                      loggedInUserData?.business
                                        ?.secondaryLanguage === "Arabic" &&
                                      row?.secondaryUnitTitle ? (
                                        truncDynCell(row?.secondaryUnitTitle)
                                      ) : (
                                        truncDynCell(row?.unitTitle)
                                      )
                                    ) : (
                                      // End of Titles change on Arabic
                                      truncDynCell(
                                        t(row[column.id]),
                                        column.maxWidth,
                                      )
                                    )}
                                  </TableCell>
                                ))}
                            </TableRow>
                            {canExpandRow ? (
                              <TableRow>
                                <TableCell
                                  colSpan={
                                    columns.length +
                                    (showCheckbox ? 1 : 0) +
                                    (expandable ? 1 : 0)
                                  }
                                  sx={{
                                    py: 0,
                                    backgroundColor: "#FAFBFD",
                                    borderBottom: rowExpanded
                                      ? undefined
                                      : "none",
                                  }}
                                >
                                  <Collapse
                                    in={rowExpanded}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box
                                      dir={isRTL ? "rtl" : "ltr"}
                                      sx={{ py: 1.5, px: 1 }}
                                    >
                                      {expandLoadingIds?.[row?.id] ? (
                                        <Box className="flex justify-center py-3">
                                          <CircularProgress size={22} />
                                        </Box>
                                      ) : (
                                        renderExpandedContent?.(row)
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {!hidePagination ? (
              <div
                style={{
                  padding: "0 20px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span className="hidden-xs mr-2 font-Saira font-[400] text-[13px]">
                  {t("rowsPerPage")}
                </span>
                <span style={{ marginRight: 10, minWidth: 60 }}>
                  {/* <strong>{tableData && tableData.pageable.pageSize}</strong>{" "} */}

                  <FormControl className="m-1 min-w-[70]" size="small">
                    <Select
                      className="font-Saira font-[500] text-[13px] h-[25px] mt-[2px]"
                      value={
                        (tableData && tableData?.pageable?.pageSize) || pageSize
                      }
                      // value={rowsPerPageOptions[2]}
                      onChange={(event) =>
                        handleChangeRowsPerPage(
                          event as React.ChangeEvent<HTMLInputElement>,
                        )
                      }
                      // style={{height:"30px"}}
                      displayEmpty
                      inputProps={{ "aria-label": "Without label" }}
                    >
                      {rowsPerPageOptions.map((option) => (
                        <MenuItem
                          className="font-Saira font-[500] text-[13px]"
                          key={option}
                          value={option}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </span>
                <span style={{ flex: 1 }}></span>

                <span
                  // style={{ marginRight: 5, whiteSpace: "nowrap", maxWidth: 300 }}
                  className="mr-2 whitespace-nowrap max-w-[300] font-Saira font-[400] text-[13px]"
                >
                  {t("page")}
                  <strong className="font-Saira ml-2 font-[700] text-[14px]">
                    {tableData && tableData.pageable
                      ? tableData.pageable.pageNumber + 1
                      : 1}
                  </strong>{" "}
                  <span className="visible-responsive"></span>
                </span>
                <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
                  {t("outOf")}

                  <strong className="ml-2 font-Saira font-[700] text-[14px]">
                    {tableData && tableData.totalPages
                      ? tableData.totalPages
                      : 1}
                  </strong>
                </span>
                <span className="hidden-responsive font-Saira mr-2 font-[400] text-[13px]">
                  {t("totalRecords")}
                  {/* )}{" "}  */}
                  <strong className="ml-2 font-Saira font-[700] text-[14px]">
                    {tableData && tableData.totalElements
                      ? tableData.totalElements
                      : 0}
                  </strong>
                </span>
                <span className="mr-2">
                  <FirstPageIcon
                    fontSize="small"
                    onClick={() => handleChangePage1("first")}
                    style={{
                      color:
                        tableData?.content?.length &&
                        tableData?.content?.length !== 0 &&
                        !tableData.first
                          ? "#1F2020"
                          : "#B8C0C6",
                      cursor:
                        tableData?.content?.length &&
                        tableData?.content?.length !== 0 &&
                        !tableData.first
                          ? "pointer"
                          : "text",
                    }}
                  />
                </span>
                <span className="mr-2">
                  <NavigateBeforeIcon
                    fontSize="small"
                    onClick={() => handleChangePage1("prev")}
                    style={{
                      color:
                        tableData?.content?.length &&
                        tableData?.content?.length !== 0 &&
                        !tableData.first
                          ? "#1F2020"
                          : "#B8C0C6",
                      cursor:
                        tableData?.content?.length &&
                        tableData?.content?.length !== 0 &&
                        !tableData.first
                          ? "pointer"
                          : "text",
                    }}
                  />
                </span>
                <span className="mr-2">
                  <NavigateNextIcon
                    fontSize="small"
                    onClick={() => handleChangePage1("next")}
                    style={{
                      color:
                        tableData?.content?.length &&
                        tableData?.content?.length !== 0 &&
                        !tableData.last
                          ? "#1F2020"
                          : "#B8C0C6",
                      cursor:
                        tableData?.content?.length &&
                        tableData?.content?.length !== 0 &&
                        !tableData.last
                          ? "pointer"
                          : "text",
                    }}
                  />
                </span>
                <span className="mr-2">
                  <LastPageIcon
                    fontSize="small"
                    onClick={() => handleChangePage1("last")}
                    style={{
                      color:
                        tableData?.content?.length &&
                        tableData?.content?.length !== 0 &&
                        !tableData.last
                          ? "#1F2020"
                          : "#B8C0C6",
                      cursor:
                        tableData?.content?.length &&
                        tableData?.content?.length !== 0 &&
                        !tableData.last
                          ? "pointer"
                          : "text",
                    }}
                  />
                </span>
              </div>
            ) : null}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BackendPaginationTable;
const rowsPerPageOptions = [5, 10, 15, 20, 25, 50, 100, 500];
const loginAsUserOptions = [
  {
    value: checkUrl("CMS"),
    label: "CMS",
  },
  {
    value: checkUrl("POS") + "/#",
    label: "POS",
  },
];
